import depsloader from "../../../depsloader";

const SEARCH_URL = "https://api.screenwork.de/geocoder/search";
const REVERSE_URL = "https://api.screenwork.de/geocoder/reverse";
const REQUEST_TIMEOUT = 20000;

const KNOWN_TYPES = ["house", "street", "locality", "district", "city", "county", "state", "country", "postcode", "other"];

function mapsKey() {
  if (typeof document === "undefined" || !document.documentElement || !document.documentElement.dataset) {
    return "";
  }

  const key = document.documentElement.dataset.mapsKey;
  return typeof key === "string" ? key.trim() : "";
}

function str(v) {
  if (typeof v === "string") {
    return v.trim();
  }
  if (typeof v === "number" && isFinite(v)) {
    return String(v);
  }
  return "";
}

function cancelledError() {
  const err = new Error("geocoder request cancelled");
  err.cancelled = true;
  return err;
}

class Geocoder {
  constructor(axios) {
    this.axios = axios;
    this.axiosRequest = null;
  }

  isCancelled(err) {
    return !!(err && (err.cancelled || this.axios.isCancel(err)));
  }

  cancelAutocomplete() {
    if (this.axiosRequest != null) {
      this.axiosRequest.source.cancel();
      this.axiosRequest = null;
    }
  }

  _geoaddressText(g) {
    let s = "";
    if (g.zip && g.city && g.district) {
      s = g.zip + " " + g.city + "–" + g.district;
    } else if (g.zip && g.city) {
      s = g.zip + " " + g.city;
    } else if (g.city && g.district) {
      s = g.city + "–" + g.district;
    } else if (g.zip && g.district) {
      s = g.zip + " " + g.district;
    } else if (g.city) {
      s = g.city;
    } else if (g.district) {
      s = g.district;
    } else if (g.zip) {
      s = g.zip;
    }

    const p = [];

    if (g.street && g.number) {
      p.push(g.street + " " + g.number);
    } else if (g.street) {
      p.push(g.street);
    }

    if (s) {
      p.push(s);
    }

    if (g.state && !g.street) {
      p.push(g.state);
    }

    if (g.country && !g.street) {
      p.push(g.country);
    }

    return p.join(", ");
  }

  // Ergebnistyp bestimmen: properties.type vorrangig, osm_key/osm_value nur als Fallback
  _type(p) {
    const type = str(p.type);
    const osmKey = str(p.osm_key);
    const osmValue = str(p.osm_value);

    if (osmKey == "place" && osmValue == "postcode") {
      return "postcode";
    }

    if (KNOWN_TYPES.indexOf(type) !== -1) {
      return type;
    }

    if (osmKey == "highway") {
      return "street";
    }
    if (osmKey == "boundary" || (osmKey == "place" && (osmValue == "suburb" || osmValue == "quarter" || osmValue == "neighbourhood"))) {
      return "district";
    }
    if (osmKey == "place" && (osmValue == "city" || osmValue == "town" || osmValue == "village" || osmValue == "hamlet")) {
      return "city";
    }
    if (str(p.housenumber)) {
      return "house";
    }

    return "other";
  }

  _geoaddress(l) {
    if (!l || typeof l !== "object" || !l.properties || typeof l.properties !== "object") {
      return null;
    }

    const coordinates = l.geometry && Array.isArray(l.geometry.coordinates) ? l.geometry.coordinates : [];
    const lng = Number(coordinates[0]);
    const lat = Number(coordinates[1]);
    if (!isFinite(lat) || !isFinite(lng) || lat < -90 || lat > 90 || lng < -180 || lng > 180) {
      return null;
    }

    const p = l.properties;
    const type = this._type(p);
    const name = str(p.name);

    const geoaddress = {};
    const set = (k, v) => {
      if (v) {
        geoaddress[k] = v;
      }
    };

    switch (type) {
      case "house":
        set("street", str(p.street));
        if (geoaddress["street"]) {
          set("number", str(p.housenumber));
        }
        set("zip", str(p.postcode));
        set("city", str(p.city));
        set("district", str(p.district));
        break;

      case "street":
        set("street", str(p.street) || name);
        set("zip", str(p.postcode));
        set("city", str(p.city));
        set("district", str(p.district));
        break;

      case "district":
      case "locality":
        set("zip", str(p.postcode));
        set("city", str(p.city));
        if (name && name != geoaddress["city"]) {
          set("district", name);
        } else {
          set("district", str(p.district));
        }
        break;

      case "city":
        set("zip", str(p.postcode));
        set("city", str(p.city) || name);
        break;

      case "postcode":
        set("zip", str(p.postcode) || name);
        set("city", str(p.city));
        set("district", str(p.district));
        break;

      default:
        set("street", str(p.street));
        if (geoaddress["street"]) {
          set("number", str(p.housenumber));
        }
        set("zip", str(p.postcode));
        set("city", str(p.city));
        set("district", str(p.district));
        break;
    }

    geoaddress["lat"] = lat;
    geoaddress["lng"] = lng;

    geoaddress["state"] = str(p.state) || undefined;
    geoaddress["country"] = str(p.country) || undefined;

    // Ohne Orts-/Straßenangabe den Feature-Namen nur als Beschriftung verwenden
    const text = this._geoaddressText(geoaddress);
    geoaddress["text"] = (geoaddress["street"] || geoaddress["city"] || geoaddress["district"] || geoaddress["zip"]) ? text : (name || text);

    if (!geoaddress["text"]) {
      return null;
    }

    return geoaddress;
  }

  geocode(params, searchQuery = "") {
    const key = mapsKey();
    if (!key) {
      return Promise.resolve([]);
    }

    this.cancelAutocomplete();

    const request = {
      source: this.axios.CancelToken.source()
    };
    this.axiosRequest = request;

    params.append("key", key);

    return this.axios.get(SEARCH_URL, {
      cancelToken: request.source.token,
      timeout: REQUEST_TIMEOUT,
      params
    }).then(res => {
      const results = [];
      const query = str(searchQuery);
      const isZipSearch = /^\d{4,5}$/.test(query);
      const features = res && res.data && Array.isArray(res.data.features) ? res.data.features : [];

      features.forEach(l => {
        if (!l || !l.properties) {
          return;
        }

        // Nur deutsche Ergebnisse
        if (l.properties.countrycode !== "DE") {
          return;
        }

        const geoaddress = this._geoaddress(l);
        if (!geoaddress) {
          return;
        }

        // Bei PLZ-Suche: nur Ergebnisse mit passender PLZ
        if (isZipSearch && geoaddress.zip && geoaddress.zip.indexOf(query) !== 0) {
          return;
        }

        results.push(geoaddress);
      });

      return results;
    }).catch(err => {
      if (this.axios.isCancel(err)) {
        throw cancelledError();
      }
      throw err;
    }).finally(() => {
      if (this.axiosRequest === request) {
        this.axiosRequest = null;
      }
    });
  }

  autocomplete(q) {
    const query = str(q);
    if (!query) {
      this.cancelAutocomplete();
      return Promise.resolve([]);
    }

    const params = new URLSearchParams();
    params.append("q", query);
    params.append("lang", "de");
    params.append("limit", 10);
    params.append("bbox", "5.87,47.27,15.04,55.06");

    return this.geocode(params, query);
  }

  reverse(lat, lng) {
    const key = mapsKey();
    const nLat = Number(lat);
    const nLon = Number(lng);

    if (!key) {
      return Promise.reject(new Error("maps key missing"));
    }

    if (!isFinite(nLat) || !isFinite(nLon) || nLat < -90 || nLat > 90 || nLon < -180 || nLon > 180) {
      return Promise.reject(new Error("invalid coordinates"));
    }

    return this.axios.get(REVERSE_URL, {
      timeout: REQUEST_TIMEOUT,
      params: {
        lat: nLat,
        lon: nLon,
        lang: "de",
        key: key
      }
    }).then(res => {
      const features = res && res.data && Array.isArray(res.data.features) ? res.data.features : [];
      const geoaddress = features.length > 0 ? this._geoaddress(features[0]) : null;
      if (!geoaddress) {
        throw new Error("invalid data");
      }
      return geoaddress;
    });
  }
}

function GeocoderFactory() {
  return new Promise(resolve => {
    depsloader.load("axios").then(axios => {
      resolve(new Geocoder(axios));
    })
  });
}

export { GeocoderFactory };

export default {};
